import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import type { Flow, Colaborador, Departamento, Card } from '../../types';
import { checkEditorFlowPermissions } from '../../types';
import { calcularHorasSLA, entradaNaEtapa } from '../../utils/slaUtils';
import { PencilIcon, TrashIcon, EllipsisHorizontalIcon, PlusIcon, DocumentDuplicateIcon, MagnifyingGlassIcon, SparklesIcon, RocketLaunchIcon, ArrowLeftIcon, ClipboardDocumentListIcon, UserGroupIcon, XMarkIcon, StarIcon, BriefcaseIcon, Cog6ToothIcon, LightBulbIcon, ChevronRightIcon, FolderIcon, FolderOpenIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { apiUrl } from '../../utils/apiBase';
import { useDataLoading } from '../../contexts/DataLoadingContext';
import DataLoadingSkeleton from '../common/DataLoadingSkeleton';
import ConfigFluxosGlobal from './ConfigFluxosGlobal';
import { DEFAULT_TEMPLATES, TEMPLATE_CATEGORIES, type DefaultTemplate } from './defaultTemplates';

interface FlowListProps {
  flows: Flow[];
  colaboradores?: Colaborador[];
  departamentos?: Departamento[];
  onCreateFlow: (name: string) => void;
  onSelectFlow: (flow: Flow) => void;
  onUpdateFlow: (updatedFlow: Flow) => void;
  onDeleteFlow: (flowId: string) => void;
  onCloneFlow?: (flow: Flow) => void;
  onCreateFromTemplate?: (templateData: any) => Promise<void>;
  userPermissions?: {
    canEdit: boolean;
    canAdmin: boolean;
    currentUser?: any;
    podeCriarFluxos?: boolean; // da config global de fluxos por colaborador
  };
  allFlows?: Flow[]; // Todos os fluxos (sem filtro), para o painel de config
  planoInfo?: any;  // informações do plano/trial para limites
  onNavigate?: (section: string) => void;  // para navegar para outras seções
  // Cards enxutos da empresa (/api/cards/resumo, o mesmo que a Home usa): a lista
  // de fluxos chega sem os cards, e é daqui que saem "atrasados" e "entraram hoje".
  cardsResumo?: Card[];
}

// Chip de departamento: 'todos', o id do departamento ou este marcador
const SEM_SETOR = '__sem_setor__';
// Em "Todos", separar a grade em seções por setor. Desligado: Todos = visão clássica; os chips filtram.
const AGRUPAR_TODOS_POR_SETOR = false;

const FlowList = ({ flows, colaboradores = [], departamentos = [], onCreateFlow, onSelectFlow, onUpdateFlow, onDeleteFlow, onCloneFlow, onCreateFromTemplate, userPermissions, allFlows, planoInfo, onNavigate, cardsResumo = [] }: FlowListProps) => {
  const limiteFluxos    = planoInfo?.limites?.fluxos ?? 999;
  const usoFluxosGlobal = planoInfo?.uso?.fluxos ?? flows.length;  // contagem global (todas as empresas) para verificar limite
  const usoFluxosLocal  = flows.length;  // contagem local (empresa selecionada) para exibir na badge
  const mostrarLimite   = limiteFluxos < 999;
  const limiteAtingido  = usoFluxosGlobal >= limiteFluxos;
  const trialExpirado   = planoInfo?.trial_expirado === true;
  const [newFlowName, setNewFlowName] = useState('');
  const [editingFlow, setEditingFlow] = useState<Flow | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [flowDetalhes, setFlowDetalhes] = useState<Flow | null>(null);
  const [loadingFlowId, setLoadingFlowId] = useState<string | null>(null);

  // A lista de flows chega sem os cards (excludeCards=true, ver por quê no
  // server.py) — o modal de Detalhes precisa dos cards de verdade pra contar
  // por etapa, então busca o flow completo só quando a pessoa abre o modal.
  const abrirDetalhesFlow = (flow: Flow) => {
    setFlowDetalhes(flow);
    fetch(apiUrl(`/flows/${flow.id}`))
      .then((r) => r.json())
      .then((resp) => { if (resp?.success && resp.data) setFlowDetalhes(resp.data); })
      .catch(() => undefined);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateView, setTemplateView] = useState<Flow | null>(null);
  const [templateNome, setTemplateNome] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateCategory, setTemplateCategory] = useState('todos');
  const [templateDetailExpanded, setTemplateDetailExpanded] = useState(false);
  const [showConfigGlobal, setShowConfigGlobal] = useState(false);
  const [selectedDefaultTemplate, setSelectedDefaultTemplate] = useState<DefaultTemplate | null>(null);
  const [tourStep, setTourStep] = useState(0);
  const [showTour, setShowTour] = useState(false);
  const [tourPaused, setTourPaused] = useState(false);
  const tourTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return localStorage.getItem('escalone_tpl_banner_dismissed') === '1'; } catch { return false; }
  });
  // Agrupamento visual de fluxos (parentFlowId)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('escalone_flow_groups_expanded');
      if (raw) return new Set(JSON.parse(raw));
    } catch {}
    return new Set();
  });
  const [groupPickerFor, setGroupPickerFor] = useState<Flow | null>(null);
  const [groupPickerSearch, setGroupPickerSearch] = useState('');
  const { loadingStates } = useDataLoading();

  const empresaId = useMemo(() => flows.find(f => f.empresaId)?.empresaId || '', [flows]);

  // --- Filtro por departamento (chip lembrado por usuário e empresa) ---
  const deptStorageKey = `escalone_flowlist_dept_${userPermissions?.currentUser?.id || 'anon'}_${empresaId || 'sem_empresa'}`;
  const [deptFiltro, setDeptFiltro] = useState<string>('todos');
  useEffect(() => {
    try { setDeptFiltro(localStorage.getItem(deptStorageKey) || 'todos'); } catch { setDeptFiltro('todos'); }
  }, [deptStorageKey]);
  const escolherDept = (id: string) => {
    setDeptFiltro(id);
    try { localStorage.setItem(deptStorageKey, id); } catch {}
  };

  const deptNomePorId = useMemo(() => {
    const map = new Map<string, string>();
    departamentos.forEach(d => { if (d?.id) map.set(String(d.id), d.nome || 'Sem nome'); });
    return map;
  }, [departamentos]);

  // Departamento "efetivo" do fluxo: id que não existe mais na empresa cai em Sem setor
  const deptDoFluxo = (flow: Flow): string => {
    const id = flow.departamentoId ? String(flow.departamentoId) : '';
    return id && deptNomePorId.has(id) ? id : SEM_SETOR;
  };
  const nomeDept = (id: string) => (id === SEM_SETOR ? 'Sem setor' : deptNomePorId.get(id) || 'Sem setor');

  // Chips: um por departamento que tenha fluxo, em ordem alfabética, Sem setor por último
  const deptChips = useMemo(() => {
    const contagem = new Map<string, number>();
    flows.forEach(f => {
      const d = deptDoFluxo(f);
      contagem.set(d, (contagem.get(d) || 0) + 1);
    });
    return Array.from(contagem.entries())
      .map(([id, total]) => ({ id, nome: nomeDept(id), total }))
      .sort((a, b) => {
        if (a.id === SEM_SETOR) return 1;
        if (b.id === SEM_SETOR) return -1;
        return a.nome.localeCompare(b.nome, 'pt-BR');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flows, deptNomePorId]);

  // Só vale a pena separar quando existe mais de um grupo
  const mostrarDepts = deptChips.length > 1;
  // Chip lembrado que não existe mais (departamento sem fluxos, outra empresa) volta para Todos
  const deptAtivo = mostrarDepts && deptChips.some(c => c.id === deptFiltro) ? deptFiltro : 'todos';

  // --- Atrasados e entraram hoje, por fluxo ---
  // Mesmas regras do quadro: SLA da etapa (tempoMaximoHoras, desde a última
  // entrada na etapa) ou campo data_vencimento já passado. Card arquivado não conta.
  const metricasPorFluxo = useMemo(() => {
    const resumoPorFluxo = new Map<string, Card[]>();
    cardsResumo.forEach(card => {
      const fid = String((card as any).flowId || '');
      if (!fid) return;
      const lista = resumoPorFluxo.get(fid);
      if (lista) lista.push(card); else resumoPorFluxo.set(fid, [card]);
    });

    const agora = new Date();
    const inicioHoje = new Date(agora);
    inicioHoje.setHours(0, 0, 0, 0);

    const result = new Map<string, { atrasados: number; hoje: number }>();
    flows.forEach(flow => {
      const cards = flow.cards?.length ? flow.cards : resumoPorFluxo.get(String(flow.id));
      if (!cards) return;
      const etapasPorId = new Map((flow.etapas || []).map(e => [String(e.id), e]));
      let atrasados = 0;
      let hoje = 0;
      cards.forEach(card => {
        if ((card as any).arquivado) return;

        const criado = new Date(((card as any).createdAt || (card as any).dataCriacao) as any);
        if (!isNaN(criado.getTime()) && criado >= inicioHoje) hoje++;

        const etapa = etapasPorId.get(String(card.etapaId));
        if (!etapa) return;
        let atrasado = false;
        if (etapa.tempoMaximoHoras) {
          const entrada = entradaNaEtapa(card.etapaHistory as any, String(etapa.id));
          if (entrada && calcularHorasSLA(entrada, agora, etapa.slaIgnorarFimDeSemana) > etapa.tempoMaximoHoras) {
            atrasado = true;
          }
        }
        if (!atrasado) {
          const campoVenc = etapa.campos?.find(c => c.tipo === 'data_vencimento');
          const valor = campoVenc ? card.campos?.[campoVenc.id] : undefined;
          if (valor) {
            const venc = new Date(valor);
            if (!isNaN(venc.getTime()) && venc < agora) atrasado = true;
          }
        }
        if (atrasado) atrasados++;
      });
      result.set(String(flow.id), { atrasados, hoje });
    });
    return result;
  }, [flows, cardsResumo]);

  // Limpa o loading quando o componente é desmontado
  useEffect(() => {
    return () => {
      setLoadingFlowId(null);
    };
  }, []);

  // Auto-advance tour timer
  useEffect(() => {
    if (tourTimerRef.current) {
      clearInterval(tourTimerRef.current);
      tourTimerRef.current = null;
    }
    if (showTour && !tourPaused && selectedDefaultTemplate) {
      const totalSteps = selectedDefaultTemplate.tour?.length || 0;
      if (totalSteps > 0 && tourStep < totalSteps - 1) {
        tourTimerRef.current = setInterval(() => {
          setTourStep(prev => {
            if (prev < totalSteps - 1) return prev + 1;
            setTourPaused(true);
            return prev;
          });
        }, 12000);
      }
    }
    return () => {
      if (tourTimerRef.current) {
        clearInterval(tourTimerRef.current);
        tourTimerRef.current = null;
      }
    };
  }, [showTour, tourPaused, tourStep, selectedDefaultTemplate]);

  // Estatísticas globais
  const stats = useMemo(() => {
    // totalCardsCount vem do backend quando a lista é carregada com excludeCards=true
    // (não traz os cards inteiros, só a contagem) — cards.length é o fallback
    // para quando os cards vêm completos.
    const totalCards = flows.reduce((acc, f) => acc + ((f as any).totalCardsCount ?? f.cards?.length ?? 0), 0);
    const totalEtapas = flows.reduce((acc, f) => acc + (f.etapas?.length || 0), 0);
    const allParticipantes = new Set<string>();
    flows.forEach(f => {
      f.etapas?.forEach(e => e.participantes?.forEach(id => allParticipantes.add(id)));
      if (f.responsavelId) allParticipantes.add(f.responsavelId);
    });
    return { totalCards, totalEtapas, totalColaboradores: allParticipantes.size };
  }, [flows]);

  // Cores para os avatares
  const avatarColors = [
    'bg-blue-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-red-500',
    'bg-indigo-500',
    'bg-teal-500',
  ];
  
  const getAvatarColor = (index: number): string => {
    return avatarColors[index % avatarColors.length];
  };

  const getInitials = (name: string): string => {
    if (!name) return '??';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Cores para os badges de iniciais dos fluxos - paleta ampla sem repetição
  const flowBadgeColors = [
    'bg-indigo-500',
    'bg-emerald-500',
    'bg-rose-500',
    'bg-amber-500',
    'bg-cyan-500',
    'bg-violet-600',
    'bg-orange-500',
    'bg-teal-500',
    'bg-pink-500',
    'bg-blue-600',
    'bg-lime-600',
    'bg-fuchsia-500',
    'bg-red-500',
    'bg-green-600',
    'bg-purple-600',
    'bg-yellow-500',
    'bg-sky-500',
    'bg-rose-600',
    'bg-slate-600',
    'bg-emerald-600',
  ];

  // Mapa de cores por índice do fluxo (cada fluxo recebe uma cor única)
  const flowColorMap = useMemo(() => {
    const map = new Map<string, string>();
    flows.forEach((flow, index) => {
      map.set(flow.id, flowBadgeColors[index % flowBadgeColors.length]);
    });
    return map;
  }, [flows]);

  const getFlowBadgeColor = (flowId: string): string => {
    return flowColorMap.get(flowId) || 'bg-indigo-500';
  };
  
  // Obter todos os colaboradores únicos do fluxo
  const getColaboradoresDoFluxo = (flow: Flow): Colaborador[] => {
    const participantesIds = new Set<string>();
    if (flow.responsavelId) {
      participantesIds.add(flow.responsavelId);
    }
    flow.etapas?.forEach(etapa => {
      etapa.participantes?.forEach(id => participantesIds.add(id));
    });
    return Array.from(participantesIds)
      .map(id => colaboradores.find(c => c.id === id))
      .filter((c): c is Colaborador => c !== undefined);
  };

  // Categorias para o modal de templates (baseadas nos templates prontos)
  const templateCategories = useMemo(() => {
    return TEMPLATE_CATEGORIES;
  }, []);

  // Filtrar templates prontos no modal
  const filteredDefaultTemplates = useMemo(() => {
    let result = DEFAULT_TEMPLATES;
    if (templateCategory !== 'todos') {
      result = result.filter(t => t.categoria === templateCategory);
    }
    if (templateSearch.trim()) {
      const q = templateSearch.toLowerCase().trim();
      result = result.filter(t => {
        if (t.name.toLowerCase().includes(q)) return true;
        if (t.descricao.toLowerCase().includes(q)) return true;
        if (t.etapas.some(e => e.nome.toLowerCase().includes(q))) return true;
        return false;
      });
    }
    return result;
  }, [templateCategory, templateSearch]);

  // Filtrar fluxos existentes como templates (seção "Seus Fluxos")
  const filteredFlowTemplates = useMemo(() => {
    if (templateCategory !== 'todos' && templateCategory !== 'meus_fluxos') return [];
    let result = flows;
    if (templateSearch.trim()) {
      const q = templateSearch.toLowerCase().trim();
      result = result.filter(f => {
        if (f.name.toLowerCase().includes(q)) return true;
        if (f.etapas?.some(e => e.nome.toLowerCase().includes(q))) return true;
        return false;
      });
    }
    return result;
  }, [flows, templateCategory, templateSearch]);

  // Filtro inteligente de fluxos
  const filteredFlows = useMemo(() => {
    if (!searchQuery.trim()) return flows;
    const q = searchQuery.toLowerCase().trim();
    return flows.filter(f => {
      // Busca por nome do fluxo
      if (f.name.toLowerCase().includes(q)) return true;
      // Busca por nome de etapa
      if (f.etapas?.some(e => e.nome.toLowerCase().includes(q))) return true;
      // Busca por nome de colaborador
      const colabs = getColaboradoresDoFluxo(f);
      if (colabs.some(c => c.nome.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [flows, searchQuery]);

  // Agrupamento visual: separa flows raiz e filhos por parentFlowId (só 1 nível)
  const isSearching = !!searchQuery.trim();

  // Busca + chip de departamento
  const visibleFlows = useMemo(() => {
    if (deptAtivo === 'todos') return filteredFlows;
    return filteredFlows.filter(f => deptDoFluxo(f) === deptAtivo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredFlows, deptAtivo, deptNomePorId]);

  const { rootFlows, childrenByParent } = useMemo(() => {
    const byId = new Map(visibleFlows.map(f => [f.id, f]));
    const children: Record<string, Flow[]> = {};
    const roots: Flow[] = [];
    for (const f of visibleFlows) {
      const pid = f.parentFlowId;
      // só considera como filho se o pai existe e não é o próprio flow
      if (pid && pid !== f.id && byId.has(pid)) {
        (children[pid] ||= []).push(f);
      } else {
        roots.push(f);
      }
    }
    return { rootFlows: roots, childrenByParent: children };
  }, [visibleFlows]);

  // Na visão "Todos", seções por departamento (na ordem dos chips). Sub-fluxo
  // acompanha o pai, então a seção é decidida pelo fluxo raiz.
  const secoesPorDept = useMemo(() => {
    const lista = isSearching ? visibleFlows : rootFlows;
    const porDept = new Map<string, Flow[]>();
    lista.forEach(f => {
      const d = deptDoFluxo(f);
      const arr = porDept.get(d);
      if (arr) arr.push(f); else porDept.set(d, [f]);
    });
    return deptChips
      .filter(c => porDept.has(c.id))
      .map(c => ({ id: c.id, nome: c.nome, flows: porDept.get(c.id)! }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching, visibleFlows, rootFlows, deptChips]);

  // Candidatos a pai: todos os flows (exceto o próprio) que NÃO têm parentFlowId
  // (evita aninhamento de mais de 1 nível)
  const parentCandidates = useMemo(() => {
    if (!groupPickerFor) return [] as Flow[];
    const q = groupPickerSearch.toLowerCase().trim();
    return flows.filter(f => {
      if (f.id === groupPickerFor.id) return false;
      if (f.parentFlowId) return false; // já é filho de alguém
      if (q && !f.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [flows, groupPickerFor, groupPickerSearch]);
  
  const handleCreateFlow= () => {
    if (newFlowName.trim()) {
      onCreateFlow(newFlowName);
      setNewFlowName('');
    }
  };

  // A config global de fluxos vale para todos (admins inclusive) — só o admin master é isento,
  // e essa isenção já vem resolvida em podeCriarFluxos
  const canCreateFlow = (userPermissions?.canAdmin || userPermissions?.canEdit)
    && userPermissions?.podeCriarFluxos !== false;
  
  const canEditFlow = (flow: Flow) => {
    if (userPermissions?.canAdmin) return true;
    if (userPermissions?.canEdit) {
      // Editores podem editar qualquer fluxo que eles trabalham
      return checkEditorFlowPermissions(
        userPermissions?.currentUser?.role || 'viewer',
        userPermissions?.currentUser?.id || '',
        flow.createdBy || flow.responsavelId,
        'edit'
      );
    }
    return false;
  };

  const canDeleteFlow = (flow: Flow) => {
    if (userPermissions?.canAdmin) return true;
    if (userPermissions?.canEdit) {
      // Editores só podem excluir fluxos que criaram
      return checkEditorFlowPermissions(
        userPermissions?.currentUser?.role || 'viewer',
        userPermissions?.currentUser?.id || '',
        flow.createdBy || flow.responsavelId,
        'delete'
      );
    }
    return false;
  };

  const handleEditFlow = (e: React.MouseEvent, flow: Flow) => {
    e.stopPropagation();
    setEditingFlow(flow);
    setOpenMenuId(null);
  };

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingFlow) {
      onUpdateFlow(editingFlow);
      setEditingFlow(null);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, flowId: string) => {
    e.stopPropagation();
    onDeleteFlow(flowId);
    setOpenMenuId(null);
  };

  const handleCloneClick = (e: React.MouseEvent, flow: Flow) => {
    e.stopPropagation();
    onCloneFlow?.(flow);
    setOpenMenuId(null);
  };

  const toggleMenu = (e: React.MouseEvent, flowId: string) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === flowId ? null : flowId);
  };

  // --- Agrupamento visual de fluxos ---
  const toggleGroupExpanded = (e: React.MouseEvent, flowId: string) => {
    e.stopPropagation();
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(flowId)) next.delete(flowId); else next.add(flowId);
      try { localStorage.setItem('escalone_flow_groups_expanded', JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const handleOpenGroupPicker = (e: React.MouseEvent, flow: Flow) => {
    e.stopPropagation();
    setGroupPickerFor(flow);
    setGroupPickerSearch('');
    setOpenMenuId(null);
  };

  const handleRemoveFromGroup = (e: React.MouseEvent, flow: Flow) => {
    e.stopPropagation();
    onUpdateFlow({ ...flow, parentFlowId: undefined });
    setOpenMenuId(null);
  };

  const handleChooseParent = (child: Flow, parentId: string | null) => {
    onUpdateFlow({ ...child, parentFlowId: parentId || undefined });
    if (parentId) {
      setExpandedGroups(prev => {
        const next = new Set(prev);
        next.add(parentId);
        try { localStorage.setItem('escalone_flow_groups_expanded', JSON.stringify([...next])); } catch {}
        return next;
      });
    }
    setGroupPickerFor(null);
  };

  // Verificar se está carregando fluxos
  const isLoadingFlows = loadingStates.flows;

  // =============================================
  // RENDER DE UM CARD DE FLUXO (reutilizado para raiz e filhos)
  // =============================================
  const renderFlowCard = (flow: Flow, opts?: { isChild?: boolean; childrenCount?: number; isExpanded?: boolean }) => {
    const isChild = !!opts?.isChild;
    const childrenCount = opts?.childrenCount || 0;
    const isExpanded = !!opts?.isExpanded;
    const hasChildren = childrenCount > 0;

    return (
      <div
        key={flow.id}
        onClick={() => {
          if (loadingFlowId) return;
          setLoadingFlowId(flow.id);
          onSelectFlow(flow);
        }}
        className={`group bg-white/80 backdrop-blur-md rounded-xl border p-3.5 hover:shadow-md hover:border-indigo-200 transition-all cursor-pointer relative ${
          isChild ? 'border-gray-200/70' : 'border-gray-200/50'
        } ${loadingFlowId === flow.id ? 'pointer-events-none' : ''}`}
      >
        {editingFlow?.id === flow.id ? (
          <form onSubmit={handleSaveEdit} onClick={e => e.stopPropagation()}
            className="space-y-2">
            <input
              value={editingFlow.name}
              onChange={e => setEditingFlow({ ...editingFlow, name: e.target.value })}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              autoFocus
            />
            <div className="flex gap-2">
              <button type="submit" className="flex-1 px-2.5 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">Salvar</button>
              <button type="button" onClick={() => setEditingFlow(null)} className="flex-1 px-2.5 py-1 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">Cancelar</button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex justify-between items-start mb-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <div className={`w-7 h-7 rounded-lg ${getFlowBadgeColor(flow.id)} text-white flex items-center justify-center font-bold text-xs flex-shrink-0`}>
                  {flow.name.charAt(0).toUpperCase()}
                </div>
                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors truncate">
                  {flow.name}
                </h3>
                {hasChildren && (
                  <button
                    onClick={(e) => toggleGroupExpanded(e, flow.id)}
                    className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 rounded-full pl-1.5 pr-2 py-0.5 transition-colors"
                    title={isExpanded ? 'Recolher sub-fluxos' : 'Expandir sub-fluxos'}
                  >
                    {isExpanded ? <FolderOpenIcon className="w-3 h-3" /> : <FolderIcon className="w-3 h-3" />}
                    <span>{childrenCount}</span>
                    <ChevronRightIcon className={`w-2.5 h-2.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>
                )}
                {isChild && (
                  <span className="flex-shrink-0 text-[9px] font-medium text-gray-400 uppercase tracking-wider">sub</span>
                )}
              </div>

              {canEditFlow(flow) && (
                <div className="relative flex-shrink-0">
                  <button
                    onClick={(e) => toggleMenu(e, flow.id)}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <EllipsisHorizontalIcon className="h-4 w-4" />
                  </button>

                  {openMenuId === flow.id && (
                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                      {canEditFlow(flow) && (
                        <button onClick={(e) => handleEditFlow(e, flow)} className="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors">
                          <PencilIcon className="h-3.5 w-3.5" />
                          <span>Editar</span>
                        </button>
                      )}
                      {canDeleteFlow(flow) && (
                        <button onClick={(e) => handleDeleteClick(e, flow.id)} className="w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors">
                          <TrashIcon className="h-3.5 w-3.5" />
                          <span>Excluir</span>
                        </button>
                      )}
                      {canEditFlow(flow) && (
                        <button onClick={(e) => handleCloneClick(e, flow)} className="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors">
                          <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                          <span>Clonar fluxo</span>
                        </button>
                      )}
                      {canEditFlow(flow) && (
                        <div className="my-1 border-t border-gray-100" />
                      )}
                      {canEditFlow(flow) && !flow.parentFlowId && (
                        <button onClick={(e) => handleOpenGroupPicker(e, flow)} className="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors">
                          <FolderIcon className="h-3.5 w-3.5" />
                          <span>Agrupar dentro de...</span>
                        </button>
                      )}
                      {canEditFlow(flow) && flow.parentFlowId && (
                        <button onClick={(e) => handleRemoveFromGroup(e, flow)} className="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors">
                          <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                          <span>Remover do grupo</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-500 mb-2.5">
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-400"></div>
                <span>{flow.etapas?.length || 0} etapas</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400"></div>
                <span>{(flow as any).totalCardsCount ?? flow.cards?.length ?? 0} cartões</span>
              </div>
            </div>

            {/* Atrasados (SLA/vencimento) e entraram hoje — só aparece quando o resumo de cards já chegou */}
            {(() => {
              const m = metricasPorFluxo.get(String(flow.id));
              if (!m) return null;
              if (m.atrasados === 0 && m.hoje === 0) {
                return (
                  <div className="flex items-center gap-1 text-[10px] text-emerald-600 mb-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                    <span>Nada atrasado</span>
                  </div>
                );
              }
              return (
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  {m.atrasados > 0 && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200 rounded-full"
                      title="Cartões que passaram do prazo da etapa (SLA) ou da data de vencimento"
                    >
                      ⏰ {m.atrasados} atrasado{m.atrasados > 1 ? 's' : ''}
                    </span>
                  )}
                  {m.hoje > 0 && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-sky-50 text-sky-700 border border-sky-200 rounded-full"
                      title="Cartões criados hoje"
                    >
                      +{m.hoje} hoje
                    </span>
                  )}
                </div>
              );
            })()}

            {/* Colaboradores do Fluxo */}
            {getColaboradoresDoFluxo(flow).length > 0 && (
              <div className="flex items-center gap-2 mb-2">
                <div className="flex -space-x-1.5">
                  {getColaboradoresDoFluxo(flow).slice(0, 4).map((colaborador, index) => (
                    <div
                      key={colaborador.id}
                      className="w-5 h-5 rounded-full border-[1.5px] border-white shadow-sm cursor-pointer hover:scale-110 transition-transform overflow-hidden"
                      title={colaborador.nome}
                      onClick={(e) => { e.stopPropagation(); abrirDetalhesFlow(flow); }}
                    >
                      {colaborador.foto ? (
                        <img src={colaborador.foto} alt={colaborador.nome} className="w-full h-full object-cover" />
                      ) : (
                        <div className={`w-full h-full ${getAvatarColor(index)} flex items-center justify-center text-white text-[8px] font-semibold`}>
                          {getInitials(colaborador.nome)}
                        </div>
                      )}
                    </div>
                  ))}
                  {getColaboradoresDoFluxo(flow).length > 4 && (
                    <div
                      className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-white text-[8px] font-semibold border-[1.5px] border-white shadow-sm cursor-pointer hover:scale-110 transition-transform"
                      onClick={(e) => { e.stopPropagation(); abrirDetalhesFlow(flow); }}
                    >
                      +{getColaboradoresDoFluxo(flow).length - 4}
                    </div>
                  )}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); abrirDetalhesFlow(flow); }}
                  className="text-[10px] text-indigo-500 hover:text-indigo-700 font-medium hover:underline"
                >
                  Detalhes
                </button>
              </div>
            )}

            <div className="text-[10px] text-gray-400 flex items-center gap-2 pt-1 border-t border-gray-100">
              <div className="group-hover:text-indigo-500 transition-colors flex items-center gap-1 flex-shrink-0">
                <span>Gerenciar</span>
                <svg className="w-2.5 h-2.5 transform group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              {flow.criadoPorNome && (
                <span className="ml-auto text-[9px] text-gray-300 truncate" title={`Criado por ${flow.criadoPorNome}`}>
                  por {flow.criadoPorNome}
                </span>
              )}
            </div>
          </>
        )}

        {/* Loading Overlay */}
        {loadingFlowId === flow.id && (
          <div className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-[10px] font-medium text-gray-600">Abrindo...</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Grade de cartões: na busca mostra a lista plana; fora dela, raízes com os
  // sub-fluxos expansíveis embaixo.
  const renderGrid = (lista: Flow[]) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
      {isSearching
        ? lista.map((flow) => renderFlowCard(flow))
        : lista.map((flow) => {
            const kids = childrenByParent[flow.id] || [];
            const isExpanded = expandedGroups.has(flow.id);
            return (
              <Fragment key={flow.id}>
                {renderFlowCard(flow, { childrenCount: kids.length, isExpanded })}
                {kids.length > 0 && isExpanded && (
                  <div className="col-span-full -mt-1 mb-1 pl-4 pr-1 pb-2 border-l-2 border-indigo-200 bg-indigo-50/30 rounded-r-lg">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-500 px-1 py-2 flex items-center gap-1.5">
                      <FolderOpenIcon className="w-3 h-3" />
                      <span>Sub-fluxos de "{flow.name}"</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {kids.map((kid) => renderFlowCard(kid, { isChild: true }))}
                    </div>
                  </div>
                )}
              </Fragment>
            );
          })
      }
    </div>
  );

  // =============================================
  // MAIN FLOW LIST VIEW
  // =============================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-white to-purple-50 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="space-y-5">
          {/* Header */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 px-5 py-4 shadow-sm">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  Meus Fluxos de Trabalho
                </h1>
                <p className="text-gray-500 text-xs mt-0.5">Gerencie e organize seus processos de trabalho</p>
              </div>
              
              {canCreateFlow ? (
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  {/* Badge de limite do plano */}
                  {mostrarLimite && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
                      limiteAtingido
                        ? 'bg-red-50 text-red-600 border-red-200'
                        : usoFluxosGlobal >= limiteFluxos - 1
                        ? 'bg-amber-50 text-amber-600 border-amber-200'
                        : 'bg-indigo-50 text-indigo-600 border-indigo-200'
                    }`} title={usoFluxosLocal !== usoFluxosGlobal ? `${usoFluxosLocal} nesta empresa · ${usoFluxosGlobal} total no plano` : undefined}>
                      {usoFluxosGlobal}/{limiteFluxos} fluxos
                    </span>
                  )}
                  <button
                    onClick={() => { setShowTemplates(true); setTemplateSearch(''); setTemplateCategory('todos'); }}
                    className="group/tpl relative px-4 py-1.5 text-sm font-semibold rounded-lg transition-all flex items-center gap-2 whitespace-nowrap bg-gradient-to-r from-gray-900 to-black text-white hover:from-black hover:to-gray-800 shadow-md shadow-gray-300 hover:shadow-lg hover:shadow-gray-400 hover:scale-[1.03] active:scale-[0.98] animate-[pulseGlow_2.5s_ease-in-out_infinite]"
                    title="Ver Templates Prontos"
                    disabled={trialExpirado || limiteAtingido}
                  >
                    <span className="text-base animate-[wiggle_1s_ease-in-out_infinite]">🧩</span>
                    Templates
                    <span className="ml-0.5 text-[10px] font-bold bg-white/20 px-1.5 py-0.5 rounded-full animate-pulse">{DEFAULT_TEMPLATES.length}</span>
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full animate-ping" />
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full" />
                  </button>
                  <input
                    value={newFlowName}
                    onChange={(e) => setNewFlowName(e.target.value)}
                    placeholder="Nome do novo fluxo"
                    className="flex-1 sm:w-48 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-white disabled:opacity-50"
                    onKeyPress={(e) => e.key === 'Enter' && !limiteAtingido && !trialExpirado && handleCreateFlow()}
                    disabled={trialExpirado || limiteAtingido}
                  />
                  <button
                    onClick={handleCreateFlow}
                    disabled={trialExpirado || limiteAtingido}
                    title={limiteAtingido ? `Limite de ${limiteFluxos} fluxos atingido` : undefined}
                    className="px-3 py-1.5 text-sm bg-gray-900 text-white rounded-lg hover:bg-black transition-colors shadow-sm flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PlusIcon className="w-3.5 h-3.5" />
                    Criar
                  </button>
                  {userPermissions?.canAdmin && (
                    <button
                      onClick={() => setShowConfigGlobal(true)}
                      className="px-2 py-1.5 text-sm border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 hover:text-gray-700 transition-colors"
                      title="Configurações de permissões dos fluxos"
                    >
                      <Cog6ToothIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                  🔒 Visualização apenas
                </div>
              )}
            </div>
          </div>

          {/* Barra de Pesquisa Inteligente */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <SparklesIcon className="h-4 w-4 text-indigo-400" />
            </div>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Pesquisar fluxos, etapas ou colaboradores..."
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all shadow-sm placeholder:text-gray-400"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Chips por departamento (só quando há mais de um grupo) */}
          {mostrarDepts && !isLoadingFlows && (
            <div className="flex flex-wrap items-center gap-1.5">
              {[{ id: 'todos', nome: 'Todos', total: flows.length }, ...deptChips].map(chip => {
                const ativo = deptAtivo === chip.id;
                return (
                  <button
                    key={chip.id}
                    onClick={() => escolherDept(chip.id)}
                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors whitespace-nowrap ${
                      ativo
                        ? 'bg-gray-900 text-white border-gray-900 shadow-sm'
                        : chip.id === SEM_SETOR
                        ? 'bg-white/80 text-gray-400 border-dashed border-gray-300 hover:bg-gray-50 hover:text-gray-600'
                        : 'bg-white/80 text-gray-600 border-gray-200 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200'
                    }`}
                  >
                    {chip.nome} <span className={ativo ? 'text-white/70' : 'text-gray-400'}>({chip.total})</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Quick Stats */}
          {flows.length > 0 && !searchQuery && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 px-4 py-3 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></div>
                  <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Fluxos Ativos</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">{flows.length}</div>
              </div>
              <div className="bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 px-4 py-3 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></div>
                  <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Total Cartões</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">{stats.totalCards}</div>
              </div>
              <div className="bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 px-4 py-3 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                  <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Etapas</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">{stats.totalEtapas}</div>
              </div>
              <div className="bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 px-4 py-3 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Colaboradores</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">{stats.totalColaboradores}</div>
              </div>
            </div>
          )}

          {/* Search Results Info */}
          {searchQuery && (
            <div className="flex items-center gap-2 px-1">
              <MagnifyingGlassIcon className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs text-gray-500">
                {visibleFlows.length === 0
                  ? `Nenhum resultado para "${searchQuery}"${deptAtivo !== 'todos' ? ` em ${nomeDept(deptAtivo)}` : ''}`
                  : `${visibleFlows.length} ${visibleFlows.length === 1 ? 'fluxo encontrado' : 'fluxos encontrados'} para "${searchQuery}"${deptAtivo !== 'todos' ? ` em ${nomeDept(deptAtivo)}` : ''}`
                }
              </span>
            </div>
          )}

          {/* Content */}
          {isLoadingFlows ? (
            <DataLoadingSkeleton 
              message="Carregando fluxos"
              submessage="Buscando seus fluxos de trabalho..."
            />
          ) : filteredFlows.length === 0 && !searchQuery && usoFluxosGlobal > 0 ? (
            /* Estado vazio para empresa SEM fluxos locais, mas com fluxos em outras empresas do grupo */
            <div className="text-center py-14 bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 shadow-sm">
              <div className="space-y-3">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center">
                  <ClipboardDocumentListIcon className="h-8 w-8 text-indigo-500" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Nenhum fluxo nesta empresa</h3>
                  <p className="text-gray-500 text-sm mt-1.5 max-w-sm mx-auto">
                    Seus {usoFluxosGlobal} fluxo{usoFluxosGlobal > 1 ? 's' : ''} {usoFluxosGlobal > 1 ? 'estão' : 'está'} em outra{usoFluxosGlobal > 1 ? 's' : ''} empresa{usoFluxosGlobal > 1 ? 's' : ''}.
                    Crie um novo fluxo aqui usando o campo acima.
                  </p>
                </div>
                {!limiteAtingido && !trialExpirado && canCreateFlow && (
                  <div className="pt-2">
                    <button
                      onClick={() => { setShowTemplates(true); setTemplateSearch(''); setTemplateCategory('todos'); }}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                      Usar Template
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : filteredFlows.length === 0 && !searchQuery ? (
            <div className="bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 shadow-sm overflow-hidden">
              {/* Hero */}
              <div className="text-center pt-10 pb-6 px-6">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mb-4">
                  <RocketLaunchIcon className="h-8 w-8 text-indigo-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Bem-vindo ao Escalone!</h3>
                <p className="text-gray-500 text-sm mt-1.5 max-w-md mx-auto">
                  Vamos configurar tudo para você começar. Siga os passos abaixo:
                </p>
              </div>

              {/* Steps */}
              <div className="px-6 pb-8 max-w-lg mx-auto space-y-3">
                {/* Step 1: Departamentos */}
                <div
                  className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${
                    departamentos.length > 0
                      ? 'bg-emerald-50/50 border-emerald-200'
                      : 'bg-white border-gray-200 hover:border-indigo-300 hover:shadow-sm cursor-pointer'
                  }`}
                  onClick={() => departamentos.length === 0 && onNavigate?.('departamentos')}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                    departamentos.length > 0
                      ? 'bg-emerald-500 text-white'
                      : 'bg-indigo-100 text-indigo-600'
                  }`}>
                    {departamentos.length > 0 ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <span className="text-sm font-bold">1</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${departamentos.length > 0 ? 'text-emerald-700' : 'text-gray-900'}`}>
                      Criar departamentos
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {departamentos.length > 0
                        ? `✓ ${departamentos.length} departamento${departamentos.length > 1 ? 's' : ''} criado${departamentos.length > 1 ? 's' : ''}`
                        : 'Organize sua equipe em áreas como Comercial, TI, RH...'}
                    </p>
                    {departamentos.length === 0 && (
                      <p className="text-[11px] text-indigo-500 mt-1 flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
                        Menu: <strong>Organizações</strong> → <strong>Departamentos</strong>
                      </p>
                    )}
                  </div>
                  {departamentos.length === 0 && (
                    <span className="text-indigo-500 text-xs font-medium bg-indigo-50 px-2.5 py-1 rounded-full shrink-0">
                      Criar →
                    </span>
                  )}
                </div>

                {/* Step 2: Colaboradores */}
                <div
                  className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${
                    colaboradores.length > 0
                      ? 'bg-emerald-50/50 border-emerald-200'
                      : departamentos.length === 0
                      ? 'bg-gray-50 border-gray-100 opacity-60'
                      : 'bg-white border-gray-200 hover:border-indigo-300 hover:shadow-sm cursor-pointer'
                  }`}
                  onClick={() => departamentos.length > 0 && colaboradores.length === 0 && onNavigate?.('colaboradores')}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                    colaboradores.length > 0
                      ? 'bg-emerald-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {colaboradores.length > 0 ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <span className="text-sm font-bold">2</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${colaboradores.length > 0 ? 'text-emerald-700' : 'text-gray-900'}`}>
                      Adicionar colaboradores
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {colaboradores.length > 0
                        ? `✓ ${colaboradores.length} colaborador${colaboradores.length > 1 ? 'es' : ''} adicionado${colaboradores.length > 1 ? 's' : ''}`
                        : departamentos.length === 0
                        ? 'Primeiro crie um departamento'
                        : 'Cadastre as pessoas da sua equipe'}
                    </p>
                    {departamentos.length > 0 && colaboradores.length === 0 && (
                      <p className="text-[11px] text-indigo-500 mt-1 flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
                        Menu: <strong>Organizações</strong> → <strong>Colaboradores</strong>
                      </p>
                    )}
                  </div>
                  {departamentos.length > 0 && colaboradores.length === 0 && (
                    <span className="text-indigo-500 text-xs font-medium bg-indigo-50 px-2.5 py-1 rounded-full shrink-0">
                      Criar →
                    </span>
                  )}
                </div>

                {/* Step 3: Criar fluxo */}
                <div
                  className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${
                    departamentos.length === 0 || colaboradores.length === 0
                      ? 'bg-gray-50 border-gray-100 opacity-60'
                      : 'bg-indigo-50/50 border-indigo-200'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                    departamentos.length > 0 && colaboradores.length > 0
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    <span className="text-sm font-bold">3</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${
                      departamentos.length > 0 && colaboradores.length > 0 ? 'text-indigo-700' : 'text-gray-900'
                    }`}>
                      Criar seu primeiro fluxo
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {departamentos.length > 0 && colaboradores.length > 0
                        ? 'Use o campo acima para criar ou escolha um Template pronto!'
                        : 'Complete os passos anteriores primeiro'}
                    </p>
                    {departamentos.length > 0 && colaboradores.length > 0 && (
                      <p className="text-[11px] text-indigo-500 mt-1 flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
                        Você está aqui! Digite o nome acima e clique em <strong>Criar</strong>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Dica */}
              <div className="bg-gray-50 border-t border-gray-100 px-6 py-4">
                <p className="text-xs text-gray-500 text-center">
                  💡 <strong>Dica:</strong> Um fluxo é um processo de trabalho com etapas (ex: Vendas → Proposta → Negociação → Fechamento). 
                  Cada etapa pode ter cards com dados, anexos e responsáveis.
                </p>
              </div>
            </div>
          ) : visibleFlows.length === 0 && searchQuery ? (
            <div className="text-center py-10 bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 shadow-sm">
              <div className="space-y-3">
                <div className="w-14 h-14 mx-auto bg-gray-100 rounded-2xl flex items-center justify-center">
                  <MagnifyingGlassIcon className="h-7 w-7 text-gray-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Nenhum resultado</h3>
                  <p className="text-gray-500 text-xs mt-1">
                    Tente buscar por outro nome de fluxo, etapa ou colaborador
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
            {/* Banner Promocional de Templates */}
            {!bannerDismissed && canCreateFlow && !trialExpirado && !limiteAtingido && !searchQuery && (
              <div className="relative group/banner overflow-hidden rounded-2xl border border-indigo-200/60 shadow-sm bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700">
                {/* BG pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 right-0 w-72 h-72 bg-white rounded-full -translate-y-1/2 translate-x-1/3" />
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/4" />
                </div>
                {/* Dismiss */}
                <button
                  onClick={(e) => { e.stopPropagation(); setBannerDismissed(true); try { localStorage.setItem('escalone_tpl_banner_dismissed', '1'); } catch {} }}
                  className="absolute top-3 right-3 z-10 w-6 h-6 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white/70 hover:text-white transition-all"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>

                <div className="relative flex flex-col sm:flex-row items-center gap-4 sm:gap-6 px-5 sm:px-7 py-5">
                  {/* Ícone grande */}
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20 shadow-lg">
                      <span className="text-3xl sm:text-4xl">🧩</span>
                    </div>
                  </div>

                  {/* Texto principal */}
                  <div className="flex-1 text-center sm:text-left min-w-0">
                    <h3 className="text-white font-bold text-base sm:text-lg leading-tight">
                      Crie fluxos prontos em 1 clique
                    </h3>
                    <p className="text-indigo-100 text-xs sm:text-sm mt-1 leading-snug max-w-md">
                      <strong className="text-white">{DEFAULT_TEMPLATES.length} templates</strong> profissionais para Financeiro, RH, Vendas, TI e mais — 
                      com etapas, campos e exemplos já montados.
                    </p>
                    {/* Mini preview de categorias */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2.5 justify-center sm:justify-start">
                      {TEMPLATE_CATEGORIES.filter(c => c.id !== 'todos').slice(0, 6).map(cat => (
                        <span key={cat.id} className="inline-flex items-center gap-1 text-[10px] font-medium bg-white/15 backdrop-blur-sm text-white/90 px-2 py-0.5 rounded-full border border-white/10">
                          <span>{cat.icone}</span>
                          <span>{cat.nome}</span>
                        </span>
                      ))}
                      {TEMPLATE_CATEGORIES.filter(c => c.id !== 'todos').length > 6 && (
                        <span className="text-[10px] text-indigo-200">+{TEMPLATE_CATEGORIES.filter(c => c.id !== 'todos').length - 6}</span>
                      )}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => { setShowTemplates(true); setTemplateSearch(''); setTemplateCategory('todos'); }}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-indigo-700 font-bold text-sm rounded-xl hover:bg-indigo-50 transition-all shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                    >
                      <DocumentDuplicateIcon className="w-4.5 h-4.5" />
                      Ver Templates
                      <ChevronRightIcon className="w-4 h-4 -mr-1" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* "Todos" fica na visão clássica (grade única, sem seções por setor), pedido do André 08/10/2026 */}
            {AGRUPAR_TODOS_POR_SETOR && mostrarDepts && deptAtivo === 'todos' ? (
              <div className="space-y-5">
                {secoesPorDept.map(secao => (
                  <section key={secao.id}>
                    <div className="flex items-center gap-2 px-1 mb-2">
                      <h2 className={`text-xs font-bold uppercase tracking-wider ${secao.id === SEM_SETOR ? 'text-gray-400' : 'text-gray-700'}`}>
                        {secao.nome}
                      </h2>
                      <span className="text-[10px] text-gray-400">{secao.flows.length}</span>
                      <div className="flex-1 border-t border-gray-200/70" />
                    </div>
                    {renderGrid(secao.flows)}
                  </section>
                ))}
              </div>
            ) : (
              renderGrid(isSearching ? visibleFlows : rootFlows)
            )}
            </>
          )}
        </div>
      </div>

      {/* ============================================= */}
      {/* MODAL DE TEMPLATES (Marketplace com Prontos + Seus Fluxos) */}
      {/* ============================================= */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowTemplates(false)}>
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col sm:flex-row border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Sidebar esquerda */}
            <div className="hidden sm:flex w-64 border-r border-gray-200 flex-col bg-gray-50/50 flex-shrink-0">
              {/* Header sidebar */}
              <div className="px-5 pt-5 pb-3">
                <h2 className="text-base font-bold text-gray-900">Criar fluxo</h2>
              </div>
              
              {/* Search no sidebar */}
              <div className="px-4 pb-3">
                <div className="relative">
                  <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    placeholder="Pesquisar templates..."
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all bg-white"
                  />
                </div>
              </div>

              {/* Categorias de Templates Prontos */}
              <div className="px-3 flex-1 overflow-y-auto">
                <div className="mb-2">
                  <span className="px-3 text-[10px] font-bold uppercase tracking-wider text-gray-400">Templates Prontos</span>
                </div>
                {templateCategories.map((cat) => {
                  const count = cat.id === 'todos' 
                    ? DEFAULT_TEMPLATES.length 
                    : DEFAULT_TEMPLATES.filter(t => t.categoria === cat.id).length;
                  if (cat.id !== 'todos' && count === 0) return null;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setTemplateCategory(cat.id)}
                      className={`w-full px-3 py-2 rounded-lg text-left text-sm flex items-center gap-2.5 transition-colors mb-0.5 ${
                        templateCategory === cat.id 
                          ? 'bg-indigo-50 text-indigo-700 font-semibold' 
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-base">{cat.icone}</span>
                      <span className="flex-1 truncate">{cat.nome}</span>
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{count}</span>
                    </button>
                  );
                })}

                {/* Separador + Seus fluxos */}
                {flows.length > 0 && (
                  <>
                    <div className="border-t border-gray-200 my-3" />
                    <div className="mb-2">
                      <span className="px-3 text-[10px] font-bold uppercase tracking-wider text-gray-400">Seus Fluxos</span>
                    </div>
                    <button
                      onClick={() => setTemplateCategory('meus_fluxos')}
                      className={`w-full px-3 py-2 rounded-lg text-left text-sm flex items-center gap-2.5 transition-colors mb-0.5 ${
                        templateCategory === 'meus_fluxos' 
                          ? 'bg-indigo-50 text-indigo-700 font-semibold' 
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                      <span className="flex-1">Usar fluxo como base</span>
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{flows.length}</span>
                    </button>
                  </>
                )}
              </div>

              {/* Criar do zero */}
              <div className="px-3 pb-4 space-y-2 border-t border-gray-200 pt-3">
                <button
                  onClick={() => {
                    setShowTemplates(false);
                    setTimeout(() => {
                      const input = document.querySelector('input[placeholder="Nome do novo fluxo"]') as HTMLInputElement;
                      if (input) input.focus();
                    }, 100);
                  }}
                  className="w-full px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center gap-2 font-medium"
                >
                  <PlusIcon className="w-4 h-4" />
                  Criar fluxo do zero
                </button>
              </div>
            </div>

            {/* Conteúdo principal (direita) */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Header direita */}
              <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100">
                <div className="flex-1 min-w-0">
                  {/* Search mobile inline */}
                  <div className="sm:hidden mb-2">
                    <div className="relative">
                      <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        value={templateSearch}
                        onChange={(e) => setTemplateSearch(e.target.value)}
                        placeholder="Pesquisar templates..."
                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 bg-white"
                      />
                    </div>
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {templateCategory === 'todos' 
                      ? 'Templates Prontos' 
                      : templateCategory === 'meus_fluxos'
                        ? 'Seus Fluxos como Template'
                        : templateCategories.find(c => c.id === templateCategory)?.nome || 'Templates'
                    }
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {templateCategory === 'meus_fluxos' 
                      ? 'Use um de seus fluxos existentes como base para criar um novo'
                      : 'Escolha um template pronto e personalize do seu jeito'
                    }
                  </p>
                  {/* Category pills mobile */}
                  <div className="sm:hidden flex gap-2 mt-3 overflow-x-auto pb-1 -mx-1 px-1">
                    {[{ id: 'todos', nome: 'Todos', icone: '⭐' }, ...TEMPLATE_CATEGORIES.filter(c => c.id !== 'todos').slice(0, 5)].map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => setTemplateCategory(cat.id)}
                        className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          templateCategory === cat.id
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {cat.icone} {cat.nome}
                      </button>
                    ))}
                    {flows.length > 0 && (
                      <button
                        onClick={() => setTemplateCategory('meus_fluxos')}
                        className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          templateCategory === 'meus_fluxos'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        📂 Meus Fluxos
                      </button>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowTemplates(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Grid de templates */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                {/* Templates Prontos */}
                {templateCategory !== 'meus_fluxos' && filteredDefaultTemplates.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
                    {filteredDefaultTemplates.map((tpl, idx) => {
                      const templateColors = [
                        'from-blue-500 to-indigo-600',
                        'from-purple-500 to-violet-600',
                        'from-emerald-500 to-teal-600',
                        'from-orange-500 to-amber-600',
                        'from-rose-500 to-pink-600',
                        'from-cyan-500 to-sky-600',
                        'from-fuchsia-500 to-purple-600',
                        'from-lime-500 to-green-600',
                      ];
                      const gradColor = templateColors[idx % templateColors.length];
                      const totalCampos = tpl.etapas.reduce((acc, e) => acc + e.campos.length, 0);
                      
                      return (
                        <button
                          key={tpl.id}
                          onClick={() => {
                            setSelectedDefaultTemplate(tpl);
                            setTemplateNome(tpl.name);
                            setShowTemplates(false);
                            setTourStep(0);
                            setShowTour(false);
                          }}
                          className="group text-left rounded-xl border border-gray-200 bg-white hover:shadow-lg hover:border-indigo-300 transition-all overflow-hidden"
                        >
                          {/* Banner */}
                          <div className={`h-24 bg-gradient-to-br ${gradColor} relative overflow-hidden`}>
                            <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-8 translate-x-8"></div>
                            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-6 -translate-x-6"></div>
                            <div className="absolute top-3 left-4 w-10 h-10 rounded-xl bg-white/25 backdrop-blur-sm flex items-center justify-center">
                              <span className="text-2xl drop-shadow-sm">{tpl.icone}</span>
                            </div>
                            <div className="absolute bottom-2 right-3 flex items-center gap-1.5">
                              <span className="text-[10px] bg-white/20 backdrop-blur-sm text-white rounded-md px-2 py-0.5 font-medium">
                                {tpl.etapas.length} etapas
                              </span>
                              <span className="text-[10px] bg-white/20 backdrop-blur-sm text-white rounded-md px-2 py-0.5 font-medium">
                                {tpl.cards.length} ex.
                              </span>
                            </div>
                          </div>
                          {/* Conteúdo */}
                          <div className="p-4">
                            <div className="flex items-start gap-2 mb-1.5">
                              <h3 className="text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors flex-1">
                                {tpl.name}
                              </h3>
                              <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-indigo-400 transition-colors flex-shrink-0 mt-0.5" />
                            </div>
                            <p className="text-[11px] text-gray-500 line-clamp-2 mb-3">
                              {tpl.descricao}
                            </p>
                            <div className="flex items-center gap-2 text-[10px] text-gray-400 mb-2">
                              <span>{tpl.etapas.length} etapas</span>
                              <span>·</span>
                              <span>{totalCampos} campos</span>
                              <span>·</span>
                              <span>{tpl.cards.length} cartões de exemplo</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {tpl.etapas.slice(0, 3).map((etapa, i) => (
                                <span key={i} className="text-[9px] px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
                                  {etapa.nome}
                                </span>
                              ))}
                              {tpl.etapas.length > 3 && (
                                <span className="text-[9px] px-2 py-0.5 bg-gray-100 rounded-full text-gray-400">
                                  +{tpl.etapas.length - 3}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Mensagem se nenhum template pronto encontrado */}
                {templateCategory !== 'meus_fluxos' && filteredDefaultTemplates.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-14 h-14 mx-auto bg-gray-100 rounded-2xl flex items-center justify-center mb-3">
                      <MagnifyingGlassIcon className="h-7 w-7 text-gray-400" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900">Nenhum template encontrado</h3>
                    <p className="text-gray-500 text-xs mt-1">Tente outro termo de busca ou categoria</p>
                  </div>
                )}

                {/* Seus Fluxos como Template */}
                {(templateCategory === 'meus_fluxos' || (templateCategory === 'todos' && flows.length > 0 && filteredDefaultTemplates.length > 0)) && (
                  <div className={templateCategory === 'todos' ? 'mt-8' : ''}>
                    {templateCategory === 'todos' && (
                      <div className="flex items-center gap-2 mb-4">
                        <div className="h-px flex-1 bg-gray-200" />
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Seus Fluxos</span>
                        <div className="h-px flex-1 bg-gray-200" />
                      </div>
                    )}
                    {filteredFlowTemplates.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 text-sm">
                        Nenhum fluxo encontrado
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
                        {filteredFlowTemplates.map((flow, idx) => {
                          const flowGradColors = [
                            'from-slate-500 to-gray-600',
                            'from-zinc-500 to-stone-600',
                            'from-neutral-500 to-gray-600',
                            'from-gray-500 to-slate-600',
                          ];
                          const gradColor = flowGradColors[idx % flowGradColors.length];
                          const colabs = getColaboradoresDoFluxo(flow);
                          const totalCampos = (flow.etapas || []).reduce((acc: number, e: any) => acc + (e.campos?.length || 0), 0);
                          
                          return (
                            <button
                              key={flow.id}
                              onClick={() => {
                                setTemplateView(flow);
                                setTemplateNome(flow.name + ' (Cópia)');
                                setShowTemplates(false);
                              }}
                              className="group text-left rounded-xl border border-gray-200 bg-white hover:shadow-lg hover:border-gray-400 transition-all overflow-hidden"
                            >
                              <div className={`h-20 bg-gradient-to-br ${gradColor} relative overflow-hidden`}>
                                <div className="absolute top-3 left-4 w-8 h-8 rounded-lg bg-white/25 backdrop-blur-sm flex items-center justify-center">
                                  <span className="text-white text-sm font-bold">{flow.name.charAt(0).toUpperCase()}</span>
                                </div>
                                {colabs.length > 0 && (
                                  <div className="absolute bottom-2 right-3 flex -space-x-1.5">
                                    {colabs.slice(0, 3).map((col, ci) => (
                                      <div key={col.id} className="w-5 h-5 rounded-full border-2 border-white/50 overflow-hidden shadow-sm">
                                        {col.foto ? (
                                          <img src={col.foto} alt={col.nome} className="w-full h-full object-cover" />
                                        ) : (
                                          <div className={`w-full h-full ${getAvatarColor(ci)} flex items-center justify-center text-white text-[7px] font-bold`}>
                                            {getInitials(col.nome)}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="p-3">
                                <h3 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-gray-700 transition-colors">
                                  {flow.name}
                                </h3>
                                <p className="text-[11px] text-gray-500 mb-2">
                                  {flow.etapas?.length || 0} etapas · {totalCampos} campos · {colabs.length} colaboradores
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {(flow.etapas || []).slice(0, 3).map((etapa: any, i: number) => (
                                    <span key={i} className="text-[9px] px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
                                      {etapa.nome}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* MODAL DE DETALHE DE TEMPLATE PRONTO */}
      {/* ============================================= */}
      {selectedDefaultTemplate && (() => {
        const tpl = selectedDefaultTemplate;
        const totalCampos = tpl.etapas.reduce((acc, e) => acc + e.campos.length, 0);
        const catInfo = TEMPLATE_CATEGORIES.find(c => c.id === tpl.categoria);
        const categoryName = catInfo?.nome || 'Geral';
        const categoryIcon = catInfo?.icone || '📋';
        // Templates relacionados (mesma categoria, excluindo o atual)
        const relatedTemplates = DEFAULT_TEMPLATES
          .filter(t => t.id !== tpl.id && t.categoria === tpl.categoria)
          .slice(0, 4);
        // Gradient
        const gradients = [
          'from-blue-500 to-indigo-600',
          'from-purple-500 to-pink-600',
          'from-emerald-500 to-teal-600',
          'from-orange-500 to-red-500',
          'from-cyan-500 to-blue-600',
        ];
        const tplGradient = gradients[DEFAULT_TEMPLATES.indexOf(tpl) % gradients.length];
        // Step colors
        const stepColors = [
          'from-indigo-500 to-purple-600',
          'from-emerald-500 to-teal-600',
          'from-orange-500 to-amber-600',
          'from-rose-500 to-pink-600',
          'from-cyan-500 to-blue-600',
          'from-violet-500 to-fuchsia-600',
          'from-lime-500 to-green-600',
          'from-red-500 to-orange-600',
          'from-sky-500 to-indigo-600',
          'from-yellow-500 to-amber-600',
        ];

        return (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => { setSelectedDefaultTemplate(null); setTemplateNome(''); setTemplateDetailExpanded(false); setShowTour(false); }}>
            <div 
              className="bg-white w-full h-full sm:rounded-2xl sm:shadow-2xl sm:w-[95%] sm:max-w-5xl sm:max-h-[92vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Breadcrumb */}
              <div className="border-b border-gray-100 px-4 sm:px-6 py-2.5 bg-white flex items-center gap-2 text-sm flex-shrink-0">
                <button
                  onClick={() => { setSelectedDefaultTemplate(null); setTemplateDetailExpanded(false); setShowTour(false); setShowTemplates(true); }}
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors"
                >
                  <ArrowLeftIcon className="w-3.5 h-3.5" />
                  <span>{categoryName}</span>
                </button>
                <span className="text-gray-300">{'>'}</span>
                <span className="text-gray-600 truncate">{tpl.name}</span>
              </div>

              {/* Conteúdo scrollável */}
              <div className="flex-1 overflow-y-auto">
                {/* Hero Section */}
                <div className="px-4 sm:px-10 pt-6 sm:pt-8 pb-6">
                  <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-md mb-3">
                        <span>{categoryIcon}</span>
                        {categoryName}
                      </span>
                      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{tpl.name}</h1>
                      <p className="text-sm text-gray-500 mb-5 leading-relaxed">{tpl.descricao}</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => {
                            if (onCreateFromTemplate) {
                              const nome = templateNome.trim() || tpl.name;
                              // Etiquetas, automações e prazo do processo também vão:
                              // sem eles o fluxo nascia sem as regras do template.
                              onCreateFromTemplate({
                                name: nome,
                                descricao: tpl.descricao,
                                etapas: tpl.etapas,
                                cards: tpl.cards,
                                etiquetas: tpl.etiquetas,
                                automacoes: tpl.automacoes,
                                prazoProcesso: tpl.prazoProcesso,
                              });
                              setSelectedDefaultTemplate(null);
                              setTemplateNome('');
                              setShowTour(false);
                            }
                          }}
                          disabled={!onCreateFromTemplate}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-semibold text-sm disabled:opacity-50"
                        >
                          <DocumentDuplicateIcon className="w-4 h-4" />
                          Usar template
                        </button>
                        <button
                          onClick={() => { setShowTour(true); setTourStep(0); setTourPaused(false); }}
                          className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium"
                        >
                          <LightBulbIcon className="w-4 h-4" />
                          Tour Interativo
                        </button>
                      </div>
                    </div>
                    {/* Preview card */}
                    <div className="flex-shrink-0 w-full sm:w-72">
                      <div className={`bg-gradient-to-br ${tplGradient} rounded-xl p-5 text-white shadow-lg relative overflow-hidden`}>
                        <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-8 translate-x-8"></div>
                        <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-6 -translate-x-6"></div>
                        <div className="relative">
                          <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                              <span className="text-xl">{tpl.icone}</span>
                            </div>
                            <span className="text-white/80 text-xs font-medium uppercase tracking-wider">Visão Geral</span>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <div className="text-2xl font-bold">{tpl.etapas.length}</div>
                              <div className="text-white/70 text-[11px]">Etapas</div>
                            </div>
                            <div>
                              <div className="text-2xl font-bold">{totalCampos}</div>
                              <div className="text-white/70 text-[11px]">Campos</div>
                            </div>
                            <div>
                              <div className="text-2xl font-bold">{tpl.cards.length}</div>
                              <div className="text-white/70 text-[11px]">Exemplos</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tour Auto-Play Modal - renders as fullscreen overlay */}
                {showTour && tpl.tour && tpl.tour.length > 0 && (() => {
                  const tourEtapaIndex = tourStep >= 1 && tourStep <= tpl.etapas.length ? tourStep - 1 : -1;
                  const tourEtapa = tourEtapaIndex >= 0 ? tpl.etapas[tourEtapaIndex] : null;
                  const tourCards = tourEtapa ? tpl.cards.filter(c => c.etapaId === tourEtapa.id) : [];
                  const progress = ((tourStep + 1) / tpl.tour.length) * 100;
                  const isIntro = tourStep === 0;
                  const isTip = tourStep >= tpl.etapas.length + 1;

                  const campoIcon = (tipo: string) => {
                    switch(tipo) {
                      case 'texto': return '✏️';
                      case 'texto_longo': return '📝';
                      case 'data': case 'data_vencimento': return '📅';
                      case 'lista': return '📋';
                      case 'checkbox': case 'checkbox_single': return '☑️';
                      case 'valor': return '💲';
                      case 'anexo': return '📎';
                      case 'telefone': return '📞';
                      case 'progresso': return '📊';
                      default: return '📌';
                    }
                  };

                  return (
                    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center" onClick={(e) => { if (e.target === e.currentTarget) setShowTour(false); }}>
                      <div className="relative w-full max-w-4xl max-h-[92vh] mx-4 flex flex-col" style={{ animation: 'fadeInScale 0.3s ease-out' }}>

                        {/* Close button floating */}
                        <button onClick={() => setShowTour(false)} className="absolute -top-2 -right-2 z-10 w-10 h-10 bg-gray-800 hover:bg-gray-700 text-white rounded-full flex items-center justify-center transition-all shadow-lg border border-gray-600">
                          <XMarkIcon className="w-5 h-5" />
                        </button>

                        {/* Main card */}
                        <div className="bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[92vh]">

                          {/* Progress bar top */}
                          <div className="h-1 bg-gray-100 flex-shrink-0">
                            <div className="h-full bg-gradient-to-r from-gray-800 via-gray-600 to-gray-800 transition-all duration-700 ease-out" style={{ width: `${progress}%` }} />
                          </div>

                          {/* Auto-advance countdown bar */}
                          {!tourPaused && tourStep < tpl.tour.length - 1 && (
                            <div className="h-0.5 bg-transparent flex-shrink-0 overflow-hidden">
                              <div className="h-full bg-indigo-400" style={{ animation: 'tourAutoProgress 12s linear', width: '0%' }} key={`progress-${tourStep}`} />
                            </div>
                          )}

                          {/* Content area */}
                          <div className="flex-1 overflow-y-auto" key={`slide-${tourStep}`} style={{ animation: 'slideIn 0.4s ease-out' }}>

                            {/* INTRO slide */}
                            {isIntro && (
                              <div className="px-8 py-10 sm:py-14 text-center">
                                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-700 shadow-xl mb-6" style={{ animation: 'fadeInScale 0.5s ease-out 0.1s both' }}>
                                  <span className="text-4xl">{tpl.icone}</span>
                                </div>
                                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3" style={{ animation: 'fadeInUp 0.5s ease-out 0.2s both' }}>{tpl.name}</h2>
                                <p className="text-gray-500 text-base sm:text-lg max-w-xl mx-auto leading-relaxed mb-8" style={{ animation: 'fadeInUp 0.5s ease-out 0.3s both' }}>{tpl.tour[tourStep]}</p>

                                {/* Mini pipeline preview */}
                                <div className="flex items-center justify-center gap-1 flex-wrap" style={{ animation: 'fadeInUp 0.5s ease-out 0.4s both' }}>
                                  {tpl.etapas.map((etapa, i) => (
                                    <div key={etapa.id} className="flex items-center gap-1">
                                      {i > 0 && <span className="text-gray-300 text-xs">→</span>}
                                      <span className={`px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r ${stepColors[i % stepColors.length]} text-white shadow-sm`}>
                                        {etapa.nome}
                                      </span>
                                    </div>
                                  ))}
                                </div>

                                <div className="mt-8 text-xs text-gray-400" style={{ animation: 'fadeInUp 0.5s ease-out 0.5s both' }}>
                                  {tpl.etapas.length} etapas · {tpl.cards.length} cartões de exemplo · Avança automaticamente
                                </div>
                              </div>
                            )}

                            {/* ETAPA slide */}
                            {tourEtapa && (
                              <div className="px-6 sm:px-8 py-6">
                                {/* Etapa header */}
                                <div className={`bg-gradient-to-r ${stepColors[tourEtapaIndex % stepColors.length]} rounded-xl px-5 py-4 mb-5 flex items-center justify-between`} style={{ animation: 'fadeInUp 0.4s ease-out both' }}>
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                                      <span className="text-white font-bold text-lg">{tourEtapaIndex + 1}</span>
                                    </div>
                                    <div>
                                      <h3 className="text-white font-bold text-lg">{tourEtapa.nome}</h3>
                                      <span className="text-white/70 text-xs">{tourEtapa.campos.length} campos · {tourCards.length} cartão(ões)</span>
                                    </div>
                                  </div>
                                  <span className="text-white/50 text-sm font-medium">Etapa {tourEtapaIndex + 1}/{tpl.etapas.length}</span>
                                </div>

                                {/* Tour text */}
                                <div className="bg-gray-50 rounded-xl p-4 mb-5 border border-gray-100" style={{ animation: 'fadeInUp 0.4s ease-out 0.1s both' }}>
                                  <p className="text-sm text-gray-700 leading-relaxed">{tpl.tour[tourStep]}</p>
                                </div>

                                {/* Rich dicas - 2 col grid */}
                                {tpl.etapasDicas && tpl.etapasDicas[tourEtapaIndex] && (() => {
                                  const dica = tpl.etapasDicas[tourEtapaIndex];
                                  return (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" style={{ animation: 'fadeInUp 0.4s ease-out 0.2s both' }}>
                                      <div className="flex items-start gap-2.5 bg-indigo-50/70 rounded-xl p-3 border border-indigo-100">
                                        <span className="text-lg">👤</span>
                                        <div>
                                          <div className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider">Quem faz</div>
                                          <div className="text-xs font-semibold text-indigo-900">{dica.quemFaz}</div>
                                        </div>
                                      </div>
                                      <div className="flex items-start gap-2.5 bg-emerald-50/70 rounded-xl p-3 border border-emerald-100">
                                        <span className="text-lg">🎯</span>
                                        <div>
                                          <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider">Objetivo</div>
                                          <div className="text-xs text-emerald-900">{dica.objetivo}</div>
                                        </div>
                                      </div>
                                      {dica.automacoes && dica.automacoes.length > 0 && (
                                        <div className="bg-amber-50/70 rounded-xl p-3 border border-amber-100">
                                          <div className="flex items-center gap-1.5 mb-1.5">
                                            <span className="text-sm">⚡</span>
                                            <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Automações</span>
                                          </div>
                                          {dica.automacoes.map((auto: string, ai: number) => (
                                            <div key={ai} className="flex items-start gap-1.5 ml-5">
                                              <span className="text-amber-400 text-[10px] mt-0.5">▸</span>
                                              <span className="text-[11px] text-amber-800">{auto}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {dica.conexoes && dica.conexoes.length > 0 && (
                                        <div className="bg-purple-50/70 rounded-xl p-3 border border-purple-100">
                                          <div className="flex items-center gap-1.5 mb-1.5">
                                            <span className="text-sm">🔗</span>
                                            <span className="text-[10px] font-bold text-purple-600 uppercase tracking-wider">Conexões</span>
                                          </div>
                                          {dica.conexoes.map((con: string, ci: number) => (
                                            <div key={ci} className="flex items-start gap-1.5 ml-5">
                                              <span className="text-purple-400 text-[10px] mt-0.5">↗</span>
                                              <span className="text-[11px] text-purple-800">{con}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {dica.ondeConfigurar && (
                                        <div className="flex items-start gap-2.5 bg-blue-50/70 rounded-xl p-3 border border-blue-100 sm:col-span-2">
                                          <span className="text-lg">⚙️</span>
                                          <div>
                                            <div className="text-[10px] font-bold text-blue-500 uppercase tracking-wider">Onde configurar</div>
                                            <div className="text-[11px] text-blue-800 font-mono bg-blue-100/50 rounded px-2 py-0.5 mt-0.5">{dica.ondeConfigurar}</div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* Campos preview - compact grid */}
                                {tourEtapa.campos.length > 0 && (
                                  <div style={{ animation: 'fadeInUp 0.4s ease-out 0.3s both' }}>
                                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Campos desta etapa</div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                      {tourEtapa.campos.map((campo) => {
                                        const exemploVal = tourCards.length > 0 ? tourCards[0].valores[campo.id] : null;
                                        return (
                                          <div key={campo.id} className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                                            <div className="flex items-center gap-1 mb-0.5">
                                              <span className="text-[10px]">{campoIcon(campo.tipo)}</span>
                                              <span className="text-[10px] font-semibold text-gray-700 truncate">{campo.nome}</span>
                                            </div>
                                            <div className="text-[10px] text-gray-400 truncate">
                                              {exemploVal ? String(exemploVal).substring(0, 30) : campo.tipo}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                {/* Example cards compact */}
                                {tourCards.length > 0 && (
                                  <div className="mt-4" style={{ animation: 'fadeInUp 0.4s ease-out 0.4s both' }}>
                                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Cartões de exemplo</div>
                                    <div className="flex gap-2 overflow-x-auto pb-1">
                                      {tourCards.map((card) => (
                                        <div key={card.id} className="flex-shrink-0 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 px-3 py-2 min-w-[180px]">
                                          <h5 className="text-xs font-bold text-gray-900 truncate">📋 {card.titulo}</h5>
                                          <span className="text-[10px] text-gray-400">{Object.keys(card.valores).length} campos</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* TIP slide (final) */}
                            {isTip && (
                              <div className="px-8 py-10 sm:py-14 text-center">
                                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-xl mb-6" style={{ animation: 'fadeInScale 0.5s ease-out 0.1s both' }}>
                                  <span className="text-4xl">💡</span>
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900 mb-3" style={{ animation: 'fadeInUp 0.5s ease-out 0.2s both' }}>Dica Final</h2>
                                <p className="text-gray-600 text-base max-w-xl mx-auto leading-relaxed" style={{ animation: 'fadeInUp 0.5s ease-out 0.3s both' }}>{tpl.tour[tourStep]}</p>
                                <div className="mt-8" style={{ animation: 'fadeInUp 0.5s ease-out 0.4s both' }}>
                                  <button onClick={() => setShowTour(false)} className="px-6 py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-black transition-colors shadow-lg">
                                    Começar a usar o template →
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Bottom navigation bar */}
                          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0 bg-white">
                            {/* Left: play/pause + counter */}
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => setTourPaused(!tourPaused)}
                                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                                title={tourPaused ? 'Reproduzir' : 'Pausar'}
                              >
                                {tourPaused ? (
                                  <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                ) : (
                                  <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                                )}
                              </button>
                              <span className="text-xs text-gray-400 font-medium">{tourStep + 1} / {tpl.tour.length}</span>
                            </div>

                            {/* Center: dots */}
                            <div className="flex gap-1.5">
                              {tpl.tour.map((_: string, i: number) => (
                                <button
                                  key={i}
                                  onClick={() => { setTourStep(i); setTourPaused(true); }}
                                  className={`h-1.5 rounded-full transition-all ${
                                    i === tourStep ? 'w-6 bg-gray-900' : i < tourStep ? 'w-1.5 bg-gray-400' : 'w-1.5 bg-gray-200 hover:bg-gray-300'
                                  }`}
                                />
                              ))}
                            </div>

                            {/* Right: prev/next */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => { setTourStep(Math.max(0, tourStep - 1)); setTourPaused(true); }}
                                disabled={tourStep === 0}
                                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors disabled:opacity-30"
                              >
                                <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                              </button>
                              <button
                                onClick={() => {
                                  if (tourStep < tpl.tour.length - 1) {
                                    setTourStep(tourStep + 1);
                                    setTourPaused(true);
                                  } else {
                                    setShowTour(false);
                                  }
                                }}
                                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                                  tourStep < tpl.tour.length - 1
                                    ? 'bg-gray-900 text-white hover:bg-black'
                                    : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                              >
                                {tourStep < tpl.tour.length - 1 ? 'Próximo →' : '✓ Concluir'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Divider */}
                <div className="border-t border-gray-100"></div>

                {/* Nome customizado + detalhes */}
                <div className="px-4 sm:px-10 py-6">
                  {/* Input de nome */}
                  <div className="mb-5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Nome do seu fluxo</label>
                    <input
                      value={templateNome}
                      onChange={(e) => setTemplateNome(e.target.value)}
                      placeholder={tpl.name}
                      className="w-full sm:w-96 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
                    />
                  </div>

                  {/* Ver mais detalhes */}
                  <button 
                    onClick={() => setTemplateDetailExpanded(!templateDetailExpanded)}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors group"
                  >
                    <svg className={`w-4 h-4 transition-transform ${templateDetailExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    <span className="group-hover:underline">Ver etapas e campos do template</span>
                  </button>

                  {/* Detalhes expandidos */}
                  {templateDetailExpanded && (
                    <div className="mt-5 space-y-3">
                      <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        <ClipboardDocumentListIcon className="w-4 h-4 text-indigo-500" />
                        Etapas do Fluxo ({tpl.etapas.length})
                      </h3>
                      <div className="space-y-2">
                        {tpl.etapas.map((etapa, index) => {
                          const cardsNaEtapa = tpl.cards.filter(c => c.etapaId === etapa.id);
                          const isTourHighlighted = showTour && tourStep >= 1 && tourStep <= tpl.etapas.length && (tourStep - 1) === index;
                          return (
                            <div key={etapa.id} id={`tour-etapa-${index}`} className="relative">
                              {index < tpl.etapas.length - 1 && (
                                <div className="absolute left-[18px] top-[42px] w-0.5 h-4 bg-indigo-200"></div>
                              )}
                              <div className={`bg-gray-50 border rounded-xl p-3.5 transition-all ${
                                isTourHighlighted
                                  ? 'border-indigo-500 ring-2 ring-indigo-200 bg-indigo-50/50 shadow-md'
                                  : 'border-gray-200 hover:border-indigo-300'
                              }`}>
                                <div className="flex items-start gap-3">
                                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${stepColors[index % stepColors.length]} text-white flex items-center justify-center font-bold text-sm shadow-sm flex-shrink-0`}>
                                    {index + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                      <h4 className="text-sm font-semibold text-gray-900">{etapa.nome}</h4>
                                      <div className="flex items-center gap-3 text-[10px] text-gray-500">
                                        {cardsNaEtapa.length > 0 && <span>{cardsNaEtapa.length} ex.</span>}
                                        <span>{etapa.campos.length} campos</span>
                                      </div>
                                    </div>
                                    {etapa.campos.length > 0 && (
                                      <div className="flex flex-wrap gap-1.5 mb-1.5">
                                        {etapa.campos.map((campo) => (
                                          <span key={campo.id} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-white rounded-md border border-gray-200 text-gray-600">
                                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0"></span>
                                            {campo.nome}
                                            <span className="text-gray-400">({campo.tipo})</span>
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    {/* Cards de exemplo nesta etapa */}
                                    {cardsNaEtapa.length > 0 && (
                                      <div className="mt-2 space-y-1">
                                        {cardsNaEtapa.map(card => (
                                          <div key={card.id} className="text-[10px] bg-blue-50 border border-blue-100 rounded-md px-2 py-1 text-blue-700 flex items-center gap-1.5">
                                            <span className="w-1 h-1 rounded-full bg-blue-400 flex-shrink-0"></span>
                                            {card.titulo}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="border-t border-gray-100"></div>

                {/* Templates relacionados */}
                {relatedTemplates.length > 0 && (
                  <div className="px-4 sm:px-10 py-6">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Você também pode gostar de</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {relatedTemplates.map((rt) => {
                        const rtGradient = gradients[DEFAULT_TEMPLATES.indexOf(rt) % gradients.length];
                        return (
                          <button
                            key={rt.id}
                            onClick={() => {
                              setSelectedDefaultTemplate(rt);
                              setTemplateNome(rt.name);
                              setTemplateDetailExpanded(false);
                              setShowTour(false);
                              setTourStep(0);
                            }}
                            className="text-left bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md hover:border-gray-300 transition-all group"
                          >
                            <div className={`bg-gradient-to-br ${rtGradient} h-20 relative overflow-hidden`}>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-3xl">{rt.icone}</span>
                              </div>
                              <div className="absolute bottom-2 right-2 bg-white/20 backdrop-blur-sm rounded-md px-2 py-0.5">
                                <span className="text-[10px] text-white font-medium">{rt.etapas.length} etapas</span>
                              </div>
                            </div>
                            <div className="p-3">
                              <h4 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">{rt.name}</h4>
                              <span className="text-[11px] text-gray-500">{rt.etapas.length} etapas · {rt.cards.length} exemplos</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom Bar */}
              <div className="border-t border-gray-100 px-4 sm:px-10 py-3.5 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between flex-shrink-0">
                <span className="text-sm text-gray-600 hidden sm:inline">Não achou um template que funcione pra você?</span>
                <button
                  onClick={() => {
                    setSelectedDefaultTemplate(null);
                    setShowTour(false);
                    setShowTemplates(false);
                    setTimeout(() => {
                      const input = document.querySelector<HTMLInputElement>('input[placeholder="Nome do novo fluxo..."]');
                      input?.focus();
                    }, 100);
                  }}
                  className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition-colors flex items-center gap-1.5"
                >
                  <PlusIcon className="w-4 h-4" />
                  Criar do zero
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ============================================= */}
      {/* MODAL DE DETALHE - SEU FLUXO COMO TEMPLATE */}
      {/* ============================================= */}
      {templateView && (() => {
        const tplColabs = getColaboradoresDoFluxo(templateView);
        const totalCampos = (templateView.etapas || []).reduce((acc: number, e: any) => acc + (e.campos?.length || 0), 0);

        return (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => { setTemplateView(null); setTemplateNome(''); }}>
            <div 
              className="bg-white w-full h-full sm:rounded-2xl sm:shadow-2xl sm:w-[90%] sm:max-w-2xl sm:max-h-[80vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="border-b border-gray-100 px-6 py-4 bg-white flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    onClick={() => { setTemplateView(null); setShowTemplates(true); setTemplateCategory('meus_fluxos'); }}
                    className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                  >
                    <ArrowLeftIcon className="w-4 h-4" />
                  </button>
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-gray-900 truncate">{templateView.name}</h2>
                    <p className="text-xs text-gray-500">Usar este fluxo como base para um novo</p>
                  </div>
                </div>
                <button onClick={() => { setTemplateView(null); setTemplateNome(''); }} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Conteúdo */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Stats */}
                <div className="grid grid-cols-4 gap-3 mb-6">
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-indigo-900">{templateView.etapas?.length || 0}</div>
                    <div className="text-[10px] text-indigo-600">Etapas</div>
                  </div>
                  <div className="bg-purple-50 border border-purple-100 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-purple-900">{totalCampos}</div>
                    <div className="text-[10px] text-purple-600">Campos</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-blue-900">{tplColabs.length}</div>
                    <div className="text-[10px] text-blue-600">Pessoas</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-emerald-900">{templateView.cards?.length || 0}</div>
                    <div className="text-[10px] text-emerald-600">Cartões</div>
                  </div>
                </div>

                {/* Nome */}
                <div className="mb-5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Nome do novo fluxo</label>
                  <input
                    value={templateNome}
                    onChange={(e) => setTemplateNome(e.target.value)}
                    placeholder={templateView.name + ' (Cópia)'}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
                  />
                </div>

                {/* Etapas preview */}
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-3">Etapas que serão copiadas</h3>
                  <div className="flex flex-wrap gap-2">
                    {(templateView.etapas || []).map((etapa: any, i: number) => (
                      <span key={i} className="text-xs px-3 py-1.5 bg-gray-100 rounded-lg text-gray-700 border border-gray-200">
                        {i + 1}. {etapa.nome}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-100 px-6 py-4 bg-gray-50 flex items-center justify-end gap-3 flex-shrink-0">
                <button
                  onClick={() => { setTemplateView(null); setTemplateNome(''); }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    if (onCloneFlow) {
                      const nome = templateNome.trim() || (templateView.name + ' (Cópia)');
                      const flowClone = { ...templateView, name: nome };
                      onCloneFlow(flowClone);
                      setTemplateView(null);
                      setTemplateNome('');
                    }
                  }}
                  disabled={!onCloneFlow}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-semibold text-sm disabled:opacity-50"
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />
                  Criar a partir deste fluxo
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal de Detalhes do Fluxo */}
      {flowDetalhes && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setFlowDetalhes(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col border border-gray-200" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-indigo-500 text-white flex items-center justify-center font-bold text-xs">
                      {flowDetalhes.name.charAt(0).toUpperCase()}
                    </div>
                    <h2 className="text-sm font-bold text-gray-900">{flowDetalhes.name}</h2>
                  </div>
                  {flowDetalhes.descricao && (
                    <p className="text-gray-500 text-xs mt-1 ml-9">{flowDetalhes.descricao}</p>
                  )}
                </div>
                <button
                  onClick={() => setFlowDetalhes(null)}
                  className="p-1 hover:bg-gray-200 rounded-md transition-colors text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Conteúdo */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Estatísticas */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="bg-indigo-50 rounded-lg p-2.5 border border-indigo-100">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>
                    <span className="text-[10px] text-indigo-600 font-medium">Etapas</span>
                  </div>
                  <div className="text-xl font-bold text-indigo-900">{flowDetalhes.etapas?.length || 0}</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-2.5 border border-purple-100">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                    <span className="text-[10px] text-purple-600 font-medium">Cartões</span>
                  </div>
                  <div className="text-xl font-bold text-purple-900">{flowDetalhes.cards?.length || 0}</div>
                </div>
              </div>

              {/* Colaboradores */}
              <div className="mb-4">
                <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                  👥 Colaboradores ({getColaboradoresDoFluxo(flowDetalhes).length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {getColaboradoresDoFluxo(flowDetalhes).map((colaborador, index) => {
                    const departamento = departamentos.find(d => d.id === colaborador.departamentoId);
                    const etapasParticipantes = flowDetalhes.etapas?.filter(e => e.participantes?.includes(colaborador.id)) || [];
                    const ehResponsavel = flowDetalhes.responsavelId === colaborador.id;
                    
                    return (
                      <div
                        key={colaborador.id}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-2 hover:border-blue-300 hover:bg-blue-50/50 transition-all"
                      >
                        <div className="flex items-center gap-2">
                          {/* Avatar */}
                          <div className="w-7 h-7 rounded-full shadow-sm flex-shrink-0 overflow-hidden">
                            {colaborador.foto ? (
                              <img src={colaborador.foto} alt={colaborador.nome} className="w-full h-full object-cover" />
                            ) : (
                              <div className={`w-full h-full ${getAvatarColor(index)} flex items-center justify-center text-white text-[10px] font-bold`}>
                                {getInitials(colaborador.nome)}
                              </div>
                            )}
                          </div>
                          
                          {/* Informações */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <h4 className="font-semibold text-gray-900 text-xs truncate">
                                {colaborador.nome}
                              </h4>
                              {ehResponsavel && (
                                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded-full flex-shrink-0">
                                  RESP
                                </span>
                              )}
                            </div>
                            
                            <p className="text-[10px] text-gray-500 truncate">
                              📂 {departamento?.nome || 'Sem departamento'} · {etapasParticipantes.length} {etapasParticipantes.length === 1 ? 'etapa' : 'etapas'}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Etapas */}
              {flowDetalhes.etapas && flowDetalhes.etapas.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                    📋 Etapas ({flowDetalhes.etapas.length})
                  </h3>
                  <div className="space-y-1.5">
                    {flowDetalhes.etapas.map((etapa, index) => {
                      const cardsNaEtapa = flowDetalhes.cards?.filter(c => c.etapaId === etapa.id).length || 0;
                      return (
                        <div
                          key={etapa.id}
                          className="bg-gray-50 border border-gray-200 rounded-lg p-2 flex items-center justify-between hover:border-blue-300 hover:bg-blue-50/50 transition-all"
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-lg bg-blue-500 text-white flex items-center justify-center font-bold text-xs">
                              {index + 1}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 text-xs">{etapa.nome}</div>
                              <div className="text-[10px] text-gray-500">
                                {etapa.participantes?.length || 0} colaboradores · {cardsNaEtapa} cartões
                              </div>
                            </div>
                          </div>
                          {etapa.campos && etapa.campos.length > 0 && (
                            <div className="text-[10px] text-gray-500">
                              {etapa.campos.length} campos
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-4 py-2.5 bg-gray-50 flex justify-end">
              <button
                onClick={() => setFlowDetalhes(null)}
                className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
      {showConfigGlobal && (
        <ConfigFluxosGlobal
          flows={allFlows || flows}
          colaboradores={colaboradores}
          empresaId={empresaId}
          currentUser={userPermissions?.currentUser}
          onClose={() => setShowConfigGlobal(false)}
        />
      )}

      {/* ============================================= */}
      {/* MODAL: Escolher fluxo pai (agrupamento visual) */}
      {/* ============================================= */}
      {groupPickerFor && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setGroupPickerFor(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-4 pb-3 border-b border-gray-100">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                    <FolderIcon className="w-4 h-4 text-indigo-500" />
                    Agrupar dentro de...
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    "{groupPickerFor.name}" vai aparecer recolhido dentro do fluxo escolhido. É apenas visual — não altera nada do fluxo em si.
                  </p>
                </div>
                <button
                  onClick={() => setGroupPickerFor(null)}
                  className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="relative mt-3">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  autoFocus
                  value={groupPickerSearch}
                  onChange={(e) => setGroupPickerSearch(e.target.value)}
                  placeholder="Pesquisar fluxo..."
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all bg-white"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-2 py-2">
              {parentCandidates.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-gray-500">
                  {groupPickerSearch.trim()
                    ? 'Nenhum fluxo encontrado.'
                    : 'Nenhum outro fluxo disponível para ser pai. Crie um fluxo "pasta" (ex: "DP") primeiro.'}
                </div>
              ) : (
                parentCandidates.map((candidate) => (
                  <button
                    key={candidate.id}
                    onClick={() => handleChooseParent(groupPickerFor, candidate.id)}
                    className="w-full px-3 py-2 rounded-lg text-left hover:bg-indigo-50 transition-colors flex items-center gap-2.5"
                  >
                    <div className={`w-7 h-7 rounded-lg ${getFlowBadgeColor(candidate.id)} text-white flex items-center justify-center font-bold text-xs flex-shrink-0`}>
                      {candidate.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">{candidate.name}</div>
                      <div className="text-[10px] text-gray-400">
                        {candidate.etapas?.length || 0} etapas · {(childrenByParent[candidate.id] || []).length} sub-fluxos
                      </div>
                    </div>
                    <ChevronRightIcon className="w-4 h-4 text-gray-300 flex-shrink-0" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowList;